import React, { useState } from 'react';
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSoftphone } from '@/contexts/SoftphoneContext';

function formatDuration(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function SoftphoneWidget() {
  const { status, incomingCall, activeCall, hangup, mute } = useSoftphone();
  const [muted, setMuted] = useState(false);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    mute(next);
  };

  // Reset mute state when a call ends
  React.useEffect(() => {
    if (!activeCall) setMuted(false);
  }, [activeCall]);

  if (status === 'unavailable') return null;

  return (
    <div className="fixed bottom-4 right-4 z-[200] flex flex-col items-end gap-2">

      {/* Incoming call popup */}
      {incomingCall && (
        <div className="w-72 rounded-xl border bg-card shadow-2xl p-4 animate-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
              <PhoneIncoming className="w-5 h-5 text-emerald-600 animate-bounce" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Incoming Call
              </div>
              <div className="font-mono font-semibold truncate">{incomingCall.from}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              onClick={incomingCall.answer}
            >
              <Phone className="w-4 h-4" /> Answer
            </Button>
            <Button
              variant="destructive"
              className="flex-1 gap-2"
              onClick={incomingCall.reject}
            >
              <PhoneOff className="w-4 h-4" /> Reject
            </Button>
          </div>
        </div>
      )}

      {/* Active call bar */}
      {activeCall && !incomingCall && (
        <div className="w-72 rounded-xl border bg-card shadow-xl p-3 animate-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center gap-3 mb-2">
            <div className={cn(
              "w-8 h-8 shrink-0 rounded-full flex items-center justify-center",
              status === 'connected' ? "bg-emerald-100 dark:bg-emerald-900/40" : "bg-amber-100 dark:bg-amber-900/40"
            )}>
              <Phone className={cn(
                "w-4 h-4",
                status === 'connected' ? "text-emerald-600" : "text-amber-600"
              )} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground">
                {status === 'connected' ? 'In call' : 'Calling...'}
              </div>
              <div className="font-mono text-sm font-medium truncate">
                {activeCall.label || activeCall.to}
              </div>
            </div>
            {status === 'connected' && (
              <Badge variant="outline" className="font-mono text-xs shrink-0">
                {formatDuration(activeCall.duration)}
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "flex-1 gap-1",
                muted && "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              )}
              onClick={toggleMute}
            >
              {muted ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
              {muted ? 'Unmute' : 'Mute'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="flex-1 gap-1"
              onClick={hangup}
            >
              <PhoneOff className="w-3 h-3" /> Hang up
            </Button>
          </div>
        </div>
      )}

      {/* Status indicator pill - only shown when idle (not mid-call) */}
      {!incomingCall && !activeCall && (
        <div className={cn(
          "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border shadow-sm select-none",
          status === 'ready' && "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800",
          status === 'registering' && "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800",
          status === 'disconnected' && "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-800"
        )}>
          <span className={cn(
            "w-1.5 h-1.5 rounded-full shrink-0",
            status === 'ready' && "bg-emerald-500",
            status === 'registering' && "bg-amber-500 animate-pulse",
            status === 'disconnected' && "bg-red-500 animate-pulse"
          )} />
          {status === 'ready' && 'Softphone ready'}
          {status === 'registering' && 'Connecting softphone...'}
          {status === 'disconnected' && 'Softphone reconnecting...'}
        </div>
      )}
    </div>
  );
}
